import React, { createContext, useState, useContext, useEffect, useCallback, useRef, ReactNode } from 'react';
import { CartAPI } from '../services/api';

// Sepet öğesi türü
interface CartItem {
  id: number;
  product_id: number;
  product_name: string;
  product_image: string;
  price: number;
  quantity: number;
  user_id: string;
  created_at: string;
  updated_at: string;
}

// Sepet bağlamı türü
interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  cartTotal: number;
  loading: boolean;
  error: string | null;
  addToCart: (product: any) => Promise<void>;
  updateCartItem: (itemId: number, quantity: number) => Promise<void>;
  removeFromCart: (itemId: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

// Varsayılan değerler
const defaultCartContext: CartContextType = {
  cartItems: [],
  cartCount: 0,
  cartTotal: 0,
  loading: false,
  error: null,
  addToCart: async () => {},
  updateCartItem: async () => {},
  removeFromCart: async () => {},
  clearCart: async () => {},
  refreshCart: async () => {},
};

// Context oluştur
const CartContext = createContext<CartContextType>(defaultCartContext);

// Hook
export const useCart = () => useContext(CartContext);

// Provider bileşeni
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  // Sepeti yükle
  const refreshCart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await CartAPI.getCart();
      setCartItems(response.items);
      setCartCount(response.count);
      setCartTotal(response.total);
    } catch (err) {
      setError('Sepet yüklenirken bir hata oluştu');
      console.error('Sepet yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sepete ürün ekle
  const addToCart = useCallback(async (product: any) => {
    setLoading(true);
    setError(null);
    try {
      await CartAPI.addToCart(product);
      await refreshCart(); // Sepeti yenile
    } catch (err) {
      setError('Ürün sepete eklenirken bir hata oluştu');
      console.error('Sepete ekleme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, [refreshCart]);

  // Sepetteki ürün miktarını güncelle
  const updateCartItem = useCallback(async (itemId: number, quantity: number) => {
    setLoading(true);
    setError(null);
    try {
      await CartAPI.updateCartItem(itemId, quantity);
      await refreshCart(); // Sepeti yenile
    } catch (err) {
      setError('Sepet güncellenirken bir hata oluştu');
      console.error('Sepet güncelleme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, [refreshCart]);

  // Sepetten ürün kaldır
  const removeFromCart = useCallback(async (itemId: number) => {
    setLoading(true);
    setError(null);
    try {
      await CartAPI.removeFromCart(itemId);
      await refreshCart(); // Sepeti yenile
    } catch (err) {
      setError('Ürün sepetten kaldırılırken bir hata oluştu');
      console.error('Sepetten kaldırma hatası:', err);
    } finally {
      setLoading(false);
    }
  }, [refreshCart]);

  // Sepeti temizle
  const clearCart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await CartAPI.clearCart();
      setCartItems([]);
      setCartCount(0);
      setCartTotal(0);
    } catch (err) {
      setError('Sepet temizlenirken bir hata oluştu');
      console.error('Sepet temizleme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Bileşen yüklendiğinde sepeti getir - SADECE BİR KEZ
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // refreshCart'ı direkt çağır, dependency'ye ekleme
      const loadCart = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await CartAPI.getCart();
          setCartItems(response.items);
          setCartCount(response.count);
          setCartTotal(response.total);
        } catch (err) {
          setError('Sepet yüklenirken bir hata oluştu');
          console.error('Sepet yükleme hatası:', err);
        } finally {
          setLoading(false);
        }
      };
      
      loadCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        cartTotal,
        loading,
        error,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
